import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from './AuthContext';
import { billingAPI } from '../lib/api';
import { registerTierErrorHandler } from '../lib/tierErrors';
import { isTenantUser } from '../lib/authRoles';

const SubscriptionContext = createContext(null);

export function SubscriptionProvider({ children }) {
  const { user, isAuthenticated } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(false);
  const [upgradePrompt, setUpgradePrompt] = useState(null);

  const refresh = useCallback(async () => {
    if (!isAuthenticated || isTenantUser(user)) {
      setSubscription(null);
      return;
    }
    setLoading(true);
    try {
      const res = await billingAPI.getSubscription();
      setSubscription(res.data);
    } catch {
      setSubscription(null);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, user]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const openUpgrade = useCallback((payload) => {
    setUpgradePrompt(payload || { error_code: 'TIER_LIMIT_AI' });
  }, []);

  const closeUpgrade = useCallback(() => setUpgradePrompt(null), []);

  useEffect(() => {
    registerTierErrorHandler(openUpgrade);
    return () => registerTierErrorHandler(null);
  }, [openUpgrade]);

  const limits = subscription?.limits || {};
  const usage = subscription?.usage || {};
  const aiEnabled = Boolean(limits.ai_enabled);
  const tier = subscription?.tier || user?.subscription_tier || 'free';
  const isTrialing = (subscription?.subscription_status || user?.subscription_status) === 'trialing';

  const value = useMemo(
    () => ({
      subscription,
      loading,
      refresh,
      limits,
      usage,
      aiEnabled,
      tier,
      isTrialing,
      trialEndsAt: subscription?.trial_ends_at || user?.trial_ends_at,
      upgradePrompt,
      openUpgrade,
      closeUpgrade,
    }),
    [
      subscription,
      loading,
      refresh,
      limits,
      usage,
      aiEnabled,
      tier,
      isTrialing,
      user,
      upgradePrompt,
      openUpgrade,
      closeUpgrade,
    ],
  );

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
}

export function useSubscription() {
  const ctx = useContext(SubscriptionContext);
  if (!ctx) {
    throw new Error('useSubscription must be used within SubscriptionProvider');
  }
  return ctx;
}
